<template>
  <div  class="upload-image" :class="{'active': dragActive, 'small': size === 'small'}" @dragenter.prevent="setActive" @dragover.prevent="setActive" @dragleave.prevent="setInactive" @drop.prevent="onDrop">
    <div class="img-div"  @click="selectFile">
      <input type="file" :id="fileInputId" style="display: none" @change="onFileInputChange" :accept="acceptAttribute">
      <img v-if="file === null" class="upload-image-placeholder" src="../assets/images/upload-image.svg" />
      <img v-else-if="type === 'image' && imagePreview" class="preview-image" :src="imagePreview" :alt="$t('file_preview')" @click="openImagePreviewModal" />
      <img v-else-if="type === 'wav' || type === 'mp3'" src="../assets/images/audioFile.svg" />
      <img v-else-if="type === 'mp4'" src="../assets/images/videoFile.svg" />
      <img v-else-if="type === 'allFiles'" src="../assets/images/documentFile.svg" />
      <img v-else-if="type === 'srt'" src="../assets/images/documentFile.svg" />
    </div>
    <div class="upload-image-attention-div" v-if="file === null">
      <div @click="selectFile"><span class="orange">{{$t('click_to_upload')}}</span> <span class="gray">{{$t('drag_and_drop')}}</span></div>
      <div v-if="type === 'image' && imageEditorEnabled" @click="goToEditor"><span class="blue">{{$t('open_image_editor')}}</span></div>
      <span class="format-txt">{{formatAllowedFormats}}</span>
      <span class="format-txt" v-if="this.ratio">{{ $t('suggested_ratio')}} : {{this.ratio}}</span>
    </div>

    <div class="upload-info" v-else>
      <div class="info-left">
        <span class="info-name">{{fileInfo?.name || file.name}}</span>
        <span class="info-size">{{formatBytes(fileInfo?.size) || formatBytes(file.size)}}</span>
      </div>
      <img v-if="!chunkUploadRunning" src="@/assets/images/trash.svg" @click="removeFile" />
    </div>
    <div v-if="chunkUploadRunning">
      <span class="gray" style="justify-self: center; display: flex; font-size: 13px">{{$t('file_uploading')}}</span>
    <div class="progress-container">
      <div
          class="progress-bar"
          :style="{
        width: `${progress}%`,
        backgroundColor: '#1e3c72'
      }"
      >
        <span class="progress-text">{{ progress }}%</span>
      </div>
    </div>
    </div>
  </div>

  <!-- Image Preview Modal -->
  <ImagePreviewModal v-if="showModal" :image="imagePreview" :visible="showModal" @hide="closeImagePreviewModal" />
</template>

<script>
import ImagePreviewModal from '@/components/modals/ImagePreviewModal.vue';
import fileService from "@/service/fileService";
import {handleError} from "vue";
import {handleErrorResponse} from "@/utils/utils";

export default {
  name: 'FileDrop',
  components: {
    ImagePreviewModal
  },
  props: {
    ratio:{
      type: String,
      default:null
    },
    notBinary:{
      type: Boolean,
      default: false
    },
    onFileSelected: {
      type: Function,
      required: true
    },
    size: {
      type: String,
      default: 'normal',
    },
    type: {
      type: String,
      default: 'image',
    },
    selectedFile: {
      type: String,
      default: ''
    },
    attach: {
      type: File,
      default: null
    },
    onDelete: {
      type: Function,
      default: () => {}
    },
    multiple: {
      type: Boolean,
      default: false
    },
    imageEditorEnabled: {
      type: Boolean,
      default: true
    },
    fileInfo:{
      type:Object,
      default:null
    },
    onUploadComplete: {
      type: Function,
      default: null
    }
  },

  data() {
    return {
      progress:0,
      chunkUploadRunning:false,
      dragActive: false,
      file: null,
      imagePreview: null,
      fileInputId: null,
      showModal: false,
    }
  },
  watch: {
    attach(val) {
      this.checkAttach(val);
    },
    selectedFile: {
      handler: function (val, oldVal) {
        this.checkSelectedFile(val);
      },
      deep: true
    },

    chunkUploadRunning(val) {
      const existingIndex = this.uploadStatus.findIndex(item => item.hasOwnProperty(this.fileInputId));
      if (existingIndex !== -1) {
        this.uploadStatus[existingIndex][this.fileInputId] = val;
      } else {
        this.uploadStatus.push({ [this.fileInputId]: val });
      }
    }
  },
  created() {
    this.fileInputId = this.randomFileInputId;
    this.checkSelectedFile(this.selectedFile);
    this.checkAttach(this.attach);

    if (this.notBinary && this.fileInfo) {
      this.file = this.fileInfo;
      if (this.fileInfo.preview) {
        this.imagePreview = this.fileInfo.preview;
      }
    }
  },
  methods: {
    setActive() {
      this.dragActive = true;
    },
    setInactive() {
      this.dragActive = false;
    },
    onDrop(e) {
      this.setInactive();
      const files = e.dataTransfer.files;
      this.handleFileSelection(files);
    },
    onFileInputChange(e) {
      const files = e.target.files;

      if (files.length > 0) {
        this.handleFileSelection(files);
        e.target.value = "";
      }
    },
    selectFile() {
      if(this.showModal === true) {
        return null
      }
      else {
        document.getElementById(this.fileInputId).click();
      }
    },
    checkSelectedFile(selectedFile) {
      try {
        if (selectedFile && selectedFile !== '' && selectedFile.startsWith('http')) {
          this.file = {
            name: selectedFile.split('/').slice(-1)[0],
            size: "-",
          }

          if (this.type === 'image') {
            this.imagePreview = selectedFile;
          }
        }
      } catch (e) {}
    },
    checkAttach(attach) {
      if (attach && !this.notBinary) {
        this.file = attach;

        if (this.type === 'image') {
          const reader = new FileReader();
          reader.onload = () => {
            this.imagePreview = reader.result;
          };
          reader.readAsDataURL(attach);
        }
      }
    },
    async handleFileSelection(files) {
      if (files.length === 0) return;

      const file = files[0];
      const isValidFileType = this.validateFileType(file);

      if (!isValidFileType) {
        return this.$snackbar.add({
          text: this.$t('invalid_file_format', { format: file.type }),
          type: 'warning'
        });
      }

      if (!this.validateFileSize(file)) return;

      this.setFileReference(file);

      if (this.notBinary) {
        await this.handleChunkedUpload(file);
      } else {
        this.onFileSelected(file);
      }
    },

    validateFileType(file) {
      const mediaTypes = ['audio/wav', 'video/mp4', 'audio/mp3', 'audio/mpeg', 'mpeg'];

      return this.type === 'image' ||
             this.type === 'allFiles' ||
             this.type === 'srt' ||
             this.type === 'wav' ||
             mediaTypes.includes(file.type);
    },

    validateFileSize(file) {
      const maxSizes = {
        image: 5 * 1024 * 1024, // 5MB
        default: 200 * 1024 * 1024 // 200MB
      };

      const maxSize = this.type === 'image' ? maxSizes.image : maxSizes.default;
      const fileTypeName = this.type === 'image' ? this.$t('file_type_image') : this.$t('file_type_media');

      if (file.size > maxSize) {
        this.$snackbar.add({
          text: this.$t('file_size_limit', { fileType: fileTypeName, size: maxSize / (1024 * 1024) }),
          type: 'warning'
        });
        return false;
      }
      return true;
    },

    setFileReference(file) {
      if (!this.multiple) {
        this.file = file;
      }

      if (this.type === 'image') {
        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result;
        };
        reader.readAsDataURL(file);
      }
    },

    async handleChunkedUpload(file) {
      const CHUNK_SIZE = 1024 * 1024; // 1MB
      const chunkSize = Math.min(CHUNK_SIZE, file.size);
      const chunks = Math.ceil(file.size / chunkSize);

      this.chunkUploadRunning = true;
      this.progress = 0;

      try {
        for (let i = 0; i < chunks; i++) {
          const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
          const formData = new FormData();

          formData.append('file', chunk, file.name);
          formData.append('chunk', i);
          formData.append('chunks', chunks);
          formData.append('name', file.name);

          const response = await fileService.uploadFile(formData);
          this.onFileSelected(response.data.data.uuid);
          this.progress = Math.round(((i + 1) / chunks) * 100);
        }

        this.file = {
          name: file.name,
          size: file.size
        };

        if (this.onUploadComplete) {
          this.onUploadComplete({ name: file.name, size: file.size, preview: this.imagePreview });
        }
      } catch (error) {
        this.$snackbar.add({
          text: this.$t('upload_service_error'),
          type: 'error'
        });
        this.onFileSelected(null);
        this.file = null;
        this.imagePreview = null;
      } finally {
        this.chunkUploadRunning = false;
        this.progress = 0;
      }
    },
    removeFile() {
      this.file = null;
      this.imagePreview = null;
      this.onDelete();
    },
    formatBytes(bytes) {
      if (bytes < 1024) {
        return bytes + ' B';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(2) + ' KB';
      } else if (bytes < 1024 * 1024 * 1024) {
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
      } else {
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
      }
    },
    goToEditor() {
      window.open('/image-editor', '_blank');
    },
    openImagePreviewModal() {
      this.showModal = true;
    },
    closeImagePreviewModal() {
      this.showModal = false;
    }
  },
  computed: {
    acceptAttribute() {
      if (this.type === 'image') {
        return 'image/*';
      } else if (this.type === 'wav') {
        return 'audio/wav';
      } else if (this.type === 'mp3') {
        return 'audio/mp3';
      } else if (this.type === 'mp4') {
        return 'video/mp4';
      } else if (this.type === 'allFiles') {
        return '*/*';
      } else if (this.type === 'srt') {
        return '.srt';
      }
    },
    formatAllowedFormats() {
      if (this.type === 'image') {
        return this.$t('format_image');
      } else if (this.type === 'wav') {
        return 'WAV';
      } else if (this.type === 'mp3') {
        return 'MP3';
      } else if (this.type === 'mp4') {
        return 'MP4';
      } else if (this.type === 'allFiles') {
        return this.$t('format_any_file');
      } else if (this.type === 'srt') {
        return this.$t('format_srt');
      }
    },
    randomFileInputId() {
      return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
  }
}
</script>


<style scoped>
.upload-image{
  border-radius: 12px;
  border: 1px solid #D0D5DD;
  background: #FFF;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.upload-image.active{
  border-radius: 12px;
  border: 1px solid #4A68F8;
  background: #EBEDFB;
  width: 100%;
}
.upload-image-attention-div{
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  cursor: pointer;
}
.orange{
  display: inline-block;
  font-family: euclid_semi_bold, sans-serif;
  color: #F09460;
  font-size: 14px;
}

.blue{
  display: inline-block;
  font-family: euclid_semi_bold, sans-serif;
  color: #4A68F8;
  font-size: 10px;
}
.gray{
  display: inline-block;
  color: #667085;
  font-size: 14px;
}
.format-txt{
  white-space: pre-wrap;
  color: #98A2B3;
  text-align: center;
  font-size: 12px;
  font-style: normal;
  line-height: 16px;
}
.img-div{
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding-top: 24px;
}
.upload-image-placeholder{
  width: 72px;
  height: 72px;
}

.upload-info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  width: 100%;
}
.info-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.info-name {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #667085;
  display: block;
  word-break: break-all;
}
.info-size {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #98A2B3;
  display: block;
}
.preview-image {
  max-width: 100%;
  max-height: 100px;
}
.small .upload-image-attention-div {
  gap: 2px;
  padding: 10px;
}
.small .upload-info {
  padding: 10px;
}
.small .upload-image-placeholder{
  width: 40px;
  height: 40px;
}
.small .img-div {
  padding-top: 12px;
}
.small .info-name {
  font-size: 12px;
}
.small .info-size {
  font-size: 10px;
}
.small .orange{
  font-size: 10px;
  line-height: 12px;
}
.small .gray{
  font-size: 10px;
  line-height: 12px;
}

.small .format-txt{
  font-size: 8px;
  line-height: 12px;
}

.progress-container {
  align-self: center;
  justify-self: center;
  margin-bottom: 15px;
  width: 90%;
  height: 15px;
  background-color: #e6e6e6;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.progress-bar {
  height: 100%;
  border-radius: 15px;
  transition: width 0.5s ease-in-out;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.progress-text {
  font-size: 10px;
  color: white;
  font-weight: bold;
  margin-right: 15px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}
</style>
